import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { toErrorMessage } from './inquiriesApi';

/**
 * Okno z polem tekstu (powód albo notatka) i podpowiedziami z makiety —
 * zapisujemy tekst człowieka, podpowiedź tylko go wpisuje.
 */
export const ReasonDialog = ({
  open,
  title,
  label,
  hints = [],
  minLength = 0,
  confirmLabel,
  description,
  extra,
  onClose,
  onConfirm,
  confirmTestId,
}: {
  open: boolean;
  title: string;
  label: string;
  hints?: readonly string[];
  minLength?: number;
  confirmLabel: string;
  description?: string;
  extra?: React.ReactNode;
  onClose: () => void;
  onConfirm: (text: string) => Promise<void>;
  confirmTestId?: string;
}) => {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setText('');
      setError(null);
    }
  }, [open]);

  const ok = text.trim().length >= minLength;

  const confirm = () =>
    void (async () => {
      setBusy(true);
      try {
        await onConfirm(text.trim());
        onClose();
      } catch (e) {
        setError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        )}
        {extra}
        {hints.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {hints.map((h) => (
              <Chip key={h} size="small" label={h} onClick={() => setText(h)} />
            ))}
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label={label}
          value={text}
          onChange={(e) => setText(e.target.value)}
          helperText={minLength > 0 ? `co najmniej ${minLength} znaków` : undefined}
          slotProps={{ htmlInput: { 'data-testid': 'inquiry-reason-text' } }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" disabled={!ok || busy} onClick={confirm} data-testid={confirmTestId}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
